import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Modal, { modalIcons } from '@ott/modal';
import { l10n, l10nhtml } from '@ott/l10n';

import { trackingTypes, WHERE_CAN_USE_CROSS_SEARCH } from '../../constants';
import { HotelsConfig, Query, RenderContext } from '../../types';

import { getCurrentSearchParams } from '../../utilities/getCurrentSearchParams';
import getMultiroomParamsByConfig from '../../utilities/getMultiroomParamsByConfig';

interface Props {
  query: Query;
  hotelsConfig: HotelsConfig;
  context: RenderContext;
}

const MultiroomWarning: React.FC<Props> = (props) => {
  const { hotelsConfig, query, context } = props;

  const { hasMultiroom } = getMultiroomParamsByConfig(hotelsConfig);
  const [isHidden, setIsHidden] = useState(false);
  const dispatch = useDispatch();

  const currentParams = useMemo(() => {
    return getCurrentSearchParams(query, {
      hotelsConfig,
      canUseCrossSearch: WHERE_CAN_USE_CROSS_SEARCH.includes(context),
    });
  }, []);

  const { initialRooms, canCombineToOneRoom } = currentParams;
  const isShown = !hasMultiroom && initialRooms && initialRooms.length > 1 && !isHidden;

  useEffect(() => {
    if (isShown) {
      dispatch({ type: trackingTypes.multiroomPopup.shown, eventData: { context } });
    }
  }, []);

  if (!isShown) {
    return null;
  }

  const handleApplyOneRoomClick = () => {
    dispatch({ type: trackingTypes.multiroomPopup.applied, eventData: { context } });
    setIsHidden(true);
  };

  const handleBookRoomLaterClick = () => {
    dispatch({ type: trackingTypes.multiroomPopup.left, eventData: { context } });
    setIsHidden(true);
  };

  const applyOneRoomButton = {
    text: l10n('hotelsSearchForm.multiroomNotAvail.applyButton'),
    onClick: handleApplyOneRoomClick,
    preventAutoClose: true,
  };

  const bookRoomLater = {
    text: l10n('hotelsSearchForm.multiroomNotAvail.leaveButton'),
    onClick: handleBookRoomLaterClick,
    preventAutoClose: true,
  };

  const modalContent = {
    icon: <modalIcons.ModalWarningIcon />,
    title: l10n('hotelsSearchForm.multiroomNotAvail.title'),
    body: l10nhtml('hotelsSearchForm.multiroomNotAvail.body'),
    primaryButton: canCombineToOneRoom ? applyOneRoomButton : bookRoomLater,
    secondaryButton: canCombineToOneRoom ? bookRoomLater : null,
  };

  return (
    <Modal
      isOpen={true}
      contentOptions={{ forceVerticalButtonLayout: true }}
      preventUserClose={true}
    >
      {modalContent}
    </Modal>
  );
};

export default MultiroomWarning;
